// Thank You & bank statements — final customer-facing stage (design ref: user screenshot). Frollo link is external; completion is confirmed by the team.
import React, { useState } from 'react';
import { Check, Lock, Star, ExternalLink, Loader2, ShieldCheck, PenLine } from 'lucide-react';
import { toast } from 'sonner';

const Stars = ({ n = 5, size = 'h-3.5 w-3.5' }) => <span className="inline-flex gap-0.5" aria-label={`${n} stars`}>{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${size} ${i <= Math.round(n) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}</span>;

const INITIAL_COLOURS = ['bg-emerald-600', 'bg-violet-600', 'bg-amber-600', 'bg-sky-600', 'bg-rose-600', 'bg-indigo-600'];

export function JourneyIndicator({ bankReceived }) {
  const stages = [
    { key: 'form', label: 'Client Form', short: 'Form', state: 'done' },
    { key: 'docs', label: 'Docs', state: 'done' },
    { key: 'bank', label: 'Bank Statements', short: 'Bank', state: bankReceived ? 'done' : 'current' },
    { key: 'assess', label: 'Assessment', short: 'Assess', state: bankReceived ? 'current' : 'locked' },
  ];
  const pct = bankReceived ? 88 : 66;
  return (
    <div data-testid="ty-journey" className="mt-8">
      <ol className="grid grid-cols-4 gap-1 text-center">
        {stages.map((s) => (
          <li key={s.key} data-testid={`ty-stage-${s.key}`} data-state={s.state} className={`inline-flex items-start justify-center gap-1 text-[11px] sm:text-xs font-bold leading-tight ${s.state === 'current' ? 'text-[#2E333B]' : s.state === 'done' ? 'text-slate-600' : 'text-slate-400'}`}>
            {s.state === 'done' && <Check className="h-3 w-3 text-emerald-600 shrink-0" />}
            {s.state === 'locked' && <Lock className="h-3 w-3 shrink-0" />}
            <span className="sm:hidden whitespace-nowrap">{s.short || s.label}</span><span className="hidden sm:inline">{s.label}</span>
          </li>
        ))}
      </ol>
      <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-[#86EFAC] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function ReviewCard({ r, i }) {
  const initials = (r.name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <article className="snap-start shrink-0 w-[240px] sm:w-auto rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm" data-testid={`ty-review-${i}`}>
      <div className="flex items-center gap-2.5">
        {r.photo_url ? <img src={r.photo_url} alt="" className="h-9 w-9 rounded-full object-cover" /> : <span className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-black text-white ${INITIAL_COLOURS[i % INITIAL_COLOURS.length]}`}>{initials}</span>}
        <div className="min-w-0"><p className="text-sm font-bold text-[#2E333B] truncate">{r.name}</p><Stars n={r.stars} size="h-3 w-3" /></div>
      </div>
      <p className="mt-2.5 text-[13px] leading-relaxed text-slate-700">{r.text}</p>
    </article>
  );
}

export default function ThankYou({ payload, api, onReview }) {
  const ty = payload.thank_you || {};
  const [opening, setOpening] = useState(false);
  const bankReceived = payload.bank_statements?.status === 'received';
  const openFrollo = async () => {
    setOpening(true);
    const win = window.open(ty.frollo_url, '_blank', 'noopener');
    if (!win) window.location.assign(ty.frollo_url);
    try { await api.bankStatementsClick(); } catch { /* event only */ }
    setTimeout(() => setOpening(false), 1500);
    toast.message('Opening the secure bank statement service in a new tab');
  };
  const reviews = ty.reviews || [];
  return (
    <div className="text-center" data-testid="ob-thank-you">
      <JourneyIndicator bankReceived={bankReceived} />
      <h1 className="mt-10 text-3xl sm:text-4xl font-extrabold text-[#2E333B] tracking-tight font-[Outfit]" data-testid="ty-heading">{ty.heading || "You're Almost Done!"}</h1>
      <p className="mt-3 text-[15px] text-slate-600 leading-relaxed max-w-xl mx-auto" data-testid="ty-message">{ty.message}</p>

      {bankReceived ? (
        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900" data-testid="ty-bank-received"><strong>Bank statements received.</strong> Your application is now with our team for assessment — we&apos;ll be in touch with your tailored options.</div>
      ) : (
        <>
          <h2 className="mt-10 text-base md:text-lg font-extrabold text-[#2E333B] font-[Outfit]" data-testid="ty-next-heading">{ty.next_heading}</h2>
          <p className="mt-3 text-[15px] text-slate-600 leading-relaxed max-w-xl mx-auto">{ty.next_body}</p>
          <button type="button" onClick={openFrollo} disabled={opening} data-testid="ty-upload-bank-statements"
            className="mt-7 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-2xl bg-[#86EFAC] px-10 py-4 text-[15px] font-bold text-[#14532D] shadow-lg shadow-[#86EFAC]/40 hover:bg-[#6EE7A0] active:scale-[0.99] transition min-h-[56px] disabled:opacity-70">
            {opening ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Upload Bank Statements
          </button>
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-500" data-testid="ty-security-note"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> {ty.security_note}</p>
        </>
      )}

      {(reviews.length > 0 || ty.google_rating) && (
        <section className="mt-12" data-testid="ty-reviews">
          <div className="inline-flex flex-wrap items-center justify-center gap-2">
            <Stars n={ty.google_rating || 5} size="h-5 w-5" />
            <span className="text-2xl font-extrabold text-[#2E333B] font-[Outfit] tabular-nums" data-testid="ty-rating">{ty.google_rating}</span>
            {ty.google_review_count ? <span className="text-sm text-slate-600" data-testid="ty-review-count">Based on {ty.google_review_count} reviews</span> : null}
          </div>
          <div className="mt-5 -mx-5 px-5 sm:mx-0 sm:px-0 flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-3 overflow-x-auto sm:overflow-visible snap-x snap-mandatory pb-2 sm:pb-0 [scrollbar-width:none]" data-testid="ty-review-list">
            {reviews.map((r, i) => <ReviewCard key={i} r={r} i={i} />)}
          </div>
          <a href={ty.google_reviews_url || '#'} target="_blank" rel="noreferrer" data-testid="ty-google-link" className="ob-tap mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#2E333B]">
            <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true"><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8Z" /><path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3a7.2 7.2 0 0 1-10.8-3.8H1.3v3.1A12 12 0 0 0 12 24Z" /><path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.3a12 12 0 0 0 0 10.8l4-3.1Z" /><path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1A7.2 7.2 0 0 1 12 4.8Z" /></svg> reviews
          </a>
        </section>
      )}
      {onReview && <button type="button" onClick={onReview} data-testid="ty-review-answers" className="ob-tap mt-8 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#2E333B]"><PenLine className="h-3.5 w-3.5" /> Review or update what you told us</button>}
    </div>
  );
}
